import type { ReactNode, TextareaHTMLAttributes } from "react";
import { useId } from "react";
import { cn } from "../cn";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  /** Shown under the field. Explains what went wrong and how to fix it. */
  error?: string;
  hint?: ReactNode;
}

/**
 * A labelled multi-line field, wired exactly as `Input` is.
 *
 * It exists because some values are genuinely not one line — a PEM bundle, a
 * service-account JSON document, a list of client ids — and the screen that
 * needed the first of those would otherwise have written its own label, its own
 * hint and its own `aria-describedby`. That is the local `Field` wrapper this
 * system already threw away three of, and the half that gets forgotten every
 * time is the one a screen reader depends on.
 */
export function Textarea({
  label,
  error,
  hint,
  className,
  id,
  rows = 4,
  ...props
}: TextareaProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={fieldId} className="font-sans text-ui text-ink-2">
          {label}
        </label>
      ) : null}
      <textarea
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(error && errorId, hint && hintId) || undefined}
        className={cn(
          "rounded-[var(--radius-control)] bg-ground px-3 py-2 font-mono text-ui leading-relaxed text-ink",
          "border border-rule placeholder:text-ink-3",
          "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent",
          error && "border-critical",
          className,
        )}
        {...props}
      />
      {hint && !error ? (
        <p id={hintId} className="text-meta text-ink-3">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-meta text-critical-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}
